import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Box from 'components/Box';
import Button from 'components/clickables/Button';
import Text from 'components/Text';

import { cxHelpers } from 'util/className';

import { CHECK_ADDRESS_ERROR } from './constants';

const { cx, cxEl } = cxHelpers('CheckAddressSuggestion');

const CheckAddressSuggestion = ({
  editMode,
  mobile,
  checkAddressErrorMessage,
  checkAddressSuggestedAlternative,
  onApplySuggestedAddress,
}) => {
  const [shouldShowSuggestion, suggestedAddress] = useMemo(() => {
    const alternativeAddress = checkAddressSuggestedAlternative();
    const errorMessage = checkAddressErrorMessage();

    // When Check returns an error message that is either of type
    // "invalid address" or "unknown street", the suggested alternative
    // is equal to the address sent in the request body, so we don't
    // want to show any suggestion to the user in this case.
    if (
      editMode &&
      errorMessage &&
      errorMessage !== CHECK_ADDRESS_ERROR.messages.invalidAddress &&
      errorMessage !== CHECK_ADDRESS_ERROR.messages.unknownStreet &&
      alternativeAddress
    ) {
      return [true, alternativeAddress];
    }

    return [false, null];
  }, [editMode, checkAddressErrorMessage, checkAddressSuggestedAlternative]);

  if (shouldShowSuggestion) {
    const firstLine = suggestedAddress.get('line1');
    const city = suggestedAddress.get('city');
    const state = suggestedAddress.get('state');
    const postalCode = suggestedAddress.get('postal_code');
    const secondLine = `${city}, ${state} ${postalCode}`;

    return (
      <Box className={cx()} row mb={16}>
        <Box grow={2} basis={0} />
        <Box
          grow={6}
          basis={0}
          bradius={6}
          ml={mobile ? 6 : 0}
          mt12
          bg="blueLightest"
          className={cxEl('suggestion-box')}
          vcenter
          row={!mobile}
          column={mobile}
          hcenter={mobile}
          spacebetween
        >
          <Box
            column
            ml24
            mt16
            mb={mobile ? 4 : 12}
            mr={102}
            w={mobile ? 200 : 336}
            h={mobile ? 100 : 72}
          >
            <Text
              fw600
              lh={1.5}
              i18n="team.employee_profile.employee_information.address_errors.suggestion.title"
            />
            <Text lh={1.5}>{firstLine}</Text>
            <Text lh={1.5}>{secondLine}</Text>
          </Box>
          <Box
            w={112}
            h={32}
            mb={mobile ? 24 : 32}
            mt={mobile ? 0 : 32}
            mr={mobile ? 0 : 24}
          >
            <Button
              fullWidth
              theme="secondary-blue"
              className={cxEl('suggestion-apply')}
              i18n="team.employee_profile.employee_information.address_errors.suggestion.btn_apply"
              onClick={onApplySuggestedAddress}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

CheckAddressSuggestion.propTypes = {
  editMode: PropTypes.bool.isRequired,
  mobile: PropTypes.bool.isRequired,
  checkAddressErrorMessage: PropTypes.func.isRequired,
  checkAddressSuggestedAlternative: PropTypes.func.isRequired,
  onApplySuggestedAddress: PropTypes.func.isRequired,
};

export default CheckAddressSuggestion;
